import { useState } from "react";
import PageHero from "@/components/feature/PageHero";
import { Field, Input, Textarea, Select } from "@/components/base/FormControls";
import Alert from "@/components/base/Alert";
import Reveal from "@/components/base/Reveal";
import { BRAND } from "@/config/brand";
import { subjectOptions } from "@/mocks/public";

type Status = "idle" | "loading" | "success" | "error";

export default function ContactPage() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const data = new FormData(formEl);

    const honeypot = String(data.get("contact_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    const prenom = String(data.get("prenom") || "").trim();
    const nom = String(data.get("nom") || "").trim();
    const email = String(data.get("email") || "").trim();
    const telephone = String(data.get("telephone") || "").trim();
    const sujet = String(data.get("sujet") || "").trim();
    const message = String(data.get("message") || "").trim();

    const next: Record<string, string> = {};
    if (!prenom) next.prenom = "Le prénom est requis.";
    if (!nom) next.nom = "Le nom est requis.";
    if (!/^\S+@\S+\.\S+$/.test(email)) next.email = "Adresse e-mail invalide.";
    if (!sujet) next.sujet = "Veuillez choisir un sujet.";
    if (message.length < 10) next.message = "Votre message doit contenir au moins 10 caractères.";
    if (message.length > 500) next.message = "Votre message ne doit pas dépasser 500 caractères.";
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setStatus("loading");
    setFormError("");

    const payload = new FormData();
    payload.append("prenom", prenom);
    payload.append("nom", nom);
    payload.append("email", email);
    if (telephone) payload.append("telephone", telephone);
    payload.append("sujet", sujet);
    payload.append("message", message);

    try {
      const response = await fetch("https://readdy.ai/api/form/datr1rmoc082o2i1jkeg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const okCode = parsed?.code === "OK";
      if (!response.ok || !okCode || isSpam) {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg && !isSpam
            ? serverMsg
            : "L'envoi a échoué. Merci de réessayer dans un instant."
        );
        return;
      }
      setStatus("success");
      setErrors({});
      formEl.reset();
    } catch {
      setStatus("error");
      setFormError("Impossible de contacter le serveur. Vérifiez votre connexion et réessayez.");
    }
  };

  return (
    <div className="flex flex-col">
      <PageHero
        eyebrow="Contact"
        current="Contact"
        title="Parlons de votre projet immobilier"
        description="Une question sur les formules, la prise en main ou l'accompagnement ? Notre équipe vous répond."
      />

      <section className="py-14 md:py-20 bg-background-50">
        <div className="container-page grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
          <Reveal variant="left" className="lg:col-span-5">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950">
              Nos coordonnées
            </h2>
            <ul className="mt-6 flex flex-col gap-5">
              <li className="flex items-start gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <i className="ri-map-pin-line text-xl" aria-hidden="true" />
                </span>
                <div>
                  <p className="text-sm font-label font-semibold text-foreground-950">Adresse</p>
                  <p className="text-sm text-foreground-600">{BRAND.address}</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-secondary-50 text-secondary-600">
                  <i className="ri-mail-line text-xl" aria-hidden="true" />
                </span>
                <div>
                  <p className="text-sm font-label font-semibold text-foreground-950">E-mail</p>
                  <a href={`mailto:${BRAND.email}`} className="text-sm text-foreground-600 hover:text-foreground-900">
                    {BRAND.email}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
                  <i className="ri-phone-line text-xl" aria-hidden="true" />
                </span>
                <div>
                  <p className="text-sm font-label font-semibold text-foreground-950">Téléphone</p>
                  <a href={`tel:${BRAND.phone.replace(/\s/g, "")}`} className="text-sm text-foreground-600 hover:text-foreground-900">
                    {BRAND.phone}
                  </a>
                </div>
              </li>
            </ul>

            <div className="mt-8 overflow-hidden rounded-lg border border-background-200">
              <iframe
                title="Localisation de l'entreprise à Ouagadougou"
                aria-label="Carte de localisation à Ouagadougou"
                src="https://www.google.com/maps?q=Ouagadougou%2C%20Burkina%20Faso&output=embed"
                className="h-[260px] w-full"
              />
            </div>
          </Reveal>

          <Reveal variant="right" className="lg:col-span-7">
            <div className="rounded-lg border border-background-200 bg-background-100/50 p-5 md:p-7">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950">
                Envoyer un message
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                Remplissez le formulaire, nous vous répondons dans les meilleurs délais.
              </p>

              {status === "success" && (
                <Alert tone="success" className="mt-6" title="Message envoyé">
                  Merci ! Votre message a bien été transmis. Nous reviendrons vers vous rapidement.
                </Alert>
              )}
              {status === "error" && formError && (
                <Alert tone="error" className="mt-6" title="Échec de l'envoi">
                  {formError}
                </Alert>
              )}

              <form
                id="contact-form"
                data-readdy-form
                className="mt-6 flex flex-col gap-4"
                onSubmit={onSubmit}
                noValidate
              >
                <input
                  type="text"
                  name="contact_alt"
                  className="form-alt-field-website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Prénom" htmlFor="ct-prenom" error={errors.prenom} required>
                    <Input id="ct-prenom" name="prenom" placeholder="Votre prénom" invalid={Boolean(errors.prenom)} />
                  </Field>
                  <Field label="Nom" htmlFor="ct-nom" error={errors.nom} required>
                    <Input id="ct-nom" name="nom" placeholder="Votre nom" invalid={Boolean(errors.nom)} />
                  </Field>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="E-mail" htmlFor="ct-email" error={errors.email} required>
                    <Input id="ct-email" name="email" type="email" icon="ri-mail-line" placeholder="vous@exemple.bf" invalid={Boolean(errors.email)} />
                  </Field>
                  <Field label="Téléphone" htmlFor="ct-tel" error={errors.telephone}>
                    <Input id="ct-tel" name="telephone" icon="ri-phone-line" placeholder="+226 ..." />
                  </Field>
                </div>

                <Field label="Sujet" htmlFor="ct-sujet" error={errors.sujet} required>
                  <Select id="ct-sujet" name="sujet" options={subjectOptions} placeholder="Choisir un sujet" invalid={Boolean(errors.sujet)} />
                </Field>

                <Field label="Message" htmlFor="ct-message" error={errors.message} hint="500 caractères maximum." required>
                  <Textarea id="ct-message" name="message" maxLength={500} placeholder="Décrivez votre besoin..." invalid={Boolean(errors.message)} />
                </Field>

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="mt-2 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-label font-medium text-background-50 hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60"
                >
                  {status === "loading" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" aria-hidden="true" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      Envoyer
                      <i className="ri-send-plane-line" aria-hidden="true" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}